import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  BusinessUnitFormModal,
  BusinessUnitViewModal,
} from 'components/modals/modules/admin-module/BusinessUnitMasterModal';
import type {
  BusinessUnitFormData,
  BusinessUnitRecord,
} from 'components/modals/modules/admin-module/BusinessUnitMasterModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useBusinessUnits, useCreateBusinessUnit, useUpdateBusinessUnit, useDeleteBusinessUnit, useEntities } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: BusinessUnitFormData = {
  entityId: '',
  buCode: '',
  buName: '',
  buType: 'BRANCH',
  description: '',
};

// ============================================================================
// MAIN BUSINESS UNIT MASTER SCREEN
// ============================================================================

const BusinessUnitMasterScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useBusinessUnits();
  const { createBusinessUnit } = useCreateBusinessUnit();
  const { updateBusinessUnit } = useUpdateBusinessUnit();
  const { deleteBusinessUnit } = useDeleteBusinessUnit();
  const { data: entitiesData } = useEntities();

  // Data from API
  const businessUnits = (apiData as unknown as BusinessUnitRecord[]) || [];
  

  // Entity options for dropdown
  const entityOptions = useMemo(() => {
    
    return entitiesData.map((e: any) => ({ value: e.id, label: e.entityName }));
  }, [entitiesData]);

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [, setSelectedRows] = useState<BusinessUnitRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<BusinessUnitRecord | null>(null);
  const [formData, setFormData] = useState<BusinessUnitFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return businessUnits.filter((bu) => {
      const matchesSearch = !searchQuery ||
        bu.buCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bu.buName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bu.entityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (bu.description || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && bu.isActive) ||
        (statusFilter === 'Inactive' && !bu.isActive);

      const matchesType = typeFilter === '' || bu.buType === typeFilter;

      const matchesEntity = entityFilter === '' || bu.entityId === entityFilter;

      return matchesSearch && matchesStatus && matchesType && matchesEntity;
    });
  }, [businessUnits, searchQuery, statusFilter, typeFilter, entityFilter]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData(emptyFormData);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: BusinessUnitRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      entityId: record.entityId,
      buCode: record.buCode,
      buName: record.buName,
      buType: record.buType,
      description: record.description || '',
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: BusinessUnitRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    const action = formMode === 'create' ? 'Create' : 'Save';
    const message = formMode === 'create'
      ? 'Are you sure you want to create this branch?'
      : 'Are you sure you want to save changes to this branch?';

    setConfirmDialog({
      open: true,
      title: `${action} Branch`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
              await createBusinessUnit({
                entityId: formData.entityId,
                buCode: formData.buCode,
                buName: formData.buName,
                buType: formData.buType,
                description: formData.description || undefined,
              });
              await refetch();
            } else if (selectedRecord) {
              await updateBusinessUnit(selectedRecord.id, {
                entityId: formData.entityId,
                buCode: formData.buCode,
                buName: formData.buName,
                buType: formData.buType,
                description: formData.description || undefined,
                rowVersion: selectedRecord.rowVersion,
              });
              await refetch();
            }

          showMessage(`Branch ${formMode === 'create' ? 'created' : 'updated'} successfully`, 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'create' ? 'create' : 'update'} branch`, 'error');
        }
      },
    });
  }, [formMode, formData, businessUnits.length, selectedRecord, entityOptions, showMessage, createBusinessUnit, updateBusinessUnit, refetch]);

  const handleDelete = useCallback((record: BusinessUnitRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Branch',
      message: `Are you sure you want to delete branch "${record.buName}" (${record.buCode})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteBusinessUnit(record.id);
            await refetch();

          showMessage('Branch deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete branch', 'error');
        }
      },
    });
  }, [showMessage, deleteBusinessUnit, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: BusinessUnitRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(row),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => handleDelete(row),
      variant: 'danger' as const,
    },
  ], [handleView, handleEdit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<BusinessUnitRecord>[] = useMemo(() => [
    { id: 'entityName', header: 'Entity', accessorKey: 'entityName', size: 180 },
    { id: 'buCode', header: 'BU Code', accessorKey: 'buCode', size: 120 },
    { id: 'buName', header: 'BU Name', accessorKey: 'buName', size: 200 },
    {
      id: 'buType',
      header: 'Type',
      accessorKey: 'buType',
      size: 150,
      cell: ({ getValue }: any) => {
        const type = getValue() as string;
        const labels: Record<string, string> = {
          BRANCH: 'Branch',
          HEAD_OFFICE: 'Head Office',
          REGIONAL_OFFICE: 'Regional Office',
        };
        return labels[type] || type;
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 110,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 200,
      cell: ({ getValue }: any) => getValue() || '—',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Branch',
      onClick: handleAddNew,
      tooltip: 'Add New Branch',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // Entity filter options (for filter panel)
  const entityFilterOptions = useMemo(() => {
    const allOption = { value: '', label: 'All Entities' };
    
    return [allOption, ...entityOptions];
  }, [businessUnits, entityOptions]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Branch Master"
        icon={<IconSystem.home size={20} />}
        description="Manage branches and business units"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search branches...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error */}
        {apiError && businessUnits.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load branches</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && businessUnits.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                rowActions={getRowActions}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                onSelectionChange={setSelectedRows}
                emptyMessage="No branches found"
              />
            )}
          </div>

          {/* Filter Panel */}
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Entity"
                  options={entityFilterOptions}
                  value={entityFilter}
                  onChange={(val: string | string[] | null) => setEntityFilter((val as string) || '')}
                  placeholder="Search entities..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Search status..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="BU Type"
                  options={[
                    { value: '', label: 'All Types' },
                    { value: 'BRANCH', label: 'Branch' },
                    { value: 'HEAD_OFFICE', label: 'Head Office' },
                    { value: 'REGIONAL_OFFICE', label: 'Regional Office' },
                  ]}
                  value={typeFilter}
                  onChange={(val: string | string[] | null) => setTypeFilter((val as string) || '')}
                  placeholder="Search types..."
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setTypeFilter(''); setEntityFilter(''); }}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Form Modal */}
      <BusinessUnitFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        entityOptions={entityOptions}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <BusinessUnitViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default BusinessUnitMasterScreen;
